import {
  ImageUtils
} from "./chunk-SEDOTBEN.js";
import "./chunk-SA55FI2N.js";
import "./chunk-ZFACQRIZ.js";
import "./chunk-QHREOC7B.js";
import "./chunk-ZPQVM7W7.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  ButtonView,
  SplitButtonView,
  addToolbarToDropdown,
  createDropdown
} from "./chunk-R4FN2FPU.js";
import "./chunk-J3DZJUY2.js";
import {
  Command,
  Plugin,
  first,
  icons,
  identity_default,
  isObject_default,
  logWarning
} from "./chunk-H3OD3CNY.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle/imagestylecommand.js
var ImageStyleCommand = class extends Command {
  /**
   * Creates an instance of the image style command. When executed, the command applies one of
   * {@link module:image/imageconfig~ImageStyleConfig#options style options} to the currently selected image.
   *
   * @param editor The editor instance.
   * @param styles The style options that this command supports.
   */
  constructor(editor, styles) {
    super(editor);
    this._defaultStyles = {
      imageBlock: false,
      imageInline: false
    };
    this._styles = new Map(styles.map((style) => {
      if (style.isDefault) {
        for (const modelElementName of style.modelElements) {
          this._defaultStyles[modelElementName] = style.name;
        }
      }
      return [style.name, style];
    }));
  }
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const element = imageUtils.getClosestSelectedImageElement(this.editor.model.document.selection);
    this.isEnabled = !!element;
    if (!this.isEnabled) {
      this.value = false;
    } else if (element.hasAttribute("imageStyle")) {
      this.value = element.getAttribute("imageStyle");
    } else {
      this.value = this._defaultStyles[element.name];
    }
  }
  /**
   * Executes the command and applies the style to the currently selected image:
   *
   * ```ts
   * editor.execute( 'imageStyle', { value: 'side' } );
   * ```
   *
   * **Note**: Executing this command may change the image model element if the desired style requires an image
   * of a different type. Learn more about {@link module:image/imageconfig~ImageStyleOptionDefinition#modelElements model element}
   * configuration for the style option.
   *
   * @param options.value The name of the style (as configured in {@link module:image/imageconfig~ImageStyleConfig#options}).
   * @param options.setImageSizes Specifies whether the image `width` and `height` attributes should be set automatically.
   * The default is `true`.
   * @fires execute
   */
  execute(options = {}) {
    const editor = this.editor;
    const model = editor.model;
    const imageUtils = editor.plugins.get("ImageUtils");
    model.change((writer) => {
      const requestedStyle = options.value;
      const { setImageSizes = true } = options;
      let imageElement = imageUtils.getClosestSelectedImageElement(model.document.selection);
      if (requestedStyle && this.shouldConvertImageType(requestedStyle, imageElement)) {
        this.editor.execute(imageUtils.isBlockImage(imageElement) ? "imageTypeInline" : "imageTypeBlock", { setImageSizes });
        imageElement = imageUtils.getClosestSelectedImageElement(model.document.selection);
      }
      if (!requestedStyle || this._styles.get(requestedStyle).isDefault) {
        writer.removeAttribute("imageStyle", imageElement);
      } else {
        writer.setAttribute("imageStyle", requestedStyle, imageElement);
      }
      if (setImageSizes) {
        imageUtils.setImageNaturalSizeAttributes(imageElement);
      }
    });
  }
  /**
   * Returns `true` if requested style change would trigger the image type change.
   *
   * @param requestedStyle The name of the style (as configured in {@link module:image/imageconfig~ImageStyleConfig#options}).
   * @param imageElement The image model element.
   */
  shouldConvertImageType(requestedStyle, imageElement) {
    const supportedTypes = this._styles.get(requestedStyle).modelElements;
    return !supportedTypes.includes(imageElement.name);
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle/utils.js
var { objectFullWidth, objectInline, objectLeft, objectRight, objectCenter, objectBlockLeft, objectBlockRight } = icons;
var DEFAULT_OPTIONS = {
  // This style represents an image placed in the line of text.
  get inline() {
    return {
      name: "inline",
      title: "In line",
      icon: objectInline,
      modelElements: ["imageInline"],
      isDefault: true
    };
  },
  // This style represents an image aligned to the left and wrapped with text.
  get alignLeft() {
    return {
      name: "alignLeft",
      title: "Left aligned image",
      icon: objectLeft,
      modelElements: ["imageBlock", "imageInline"],
      className: "image-style-align-left"
    };
  },
  // This style represents an image aligned to the left.
  get alignBlockLeft() {
    return {
      name: "alignBlockLeft",
      title: "Left aligned image",
      icon: objectBlockLeft,
      modelElements: ["imageBlock"],
      className: "image-style-block-align-left"
    };
  },
  // This style represents a centered image.
  get alignCenter() {
    return {
      name: "alignCenter",
      title: "Centered image",
      icon: objectCenter,
      modelElements: ["imageBlock"],
      className: "image-style-align-center"
    };
  },
  // This style represents an image aligned to the right and wrapped with text.
  get alignRight() {
    return {
      name: "alignRight",
      title: "Right aligned image",
      icon: objectRight,
      modelElements: ["imageBlock", "imageInline"],
      className: "image-style-align-right"
    };
  },
  // This style represents an image aligned to the right.
  get alignBlockRight() {
    return {
      name: "alignBlockRight",
      title: "Right aligned image",
      icon: objectBlockRight,
      modelElements: ["imageBlock"],
      className: "image-style-block-align-right"
    };
  },
  // This option is equal to the situation when no style is applied.
  get block() {
    return {
      name: "block",
      title: "Centered image",
      icon: objectCenter,
      modelElements: ["imageBlock"],
      isDefault: true
    };
  },
  // This represents a side image.
  get side() {
    return {
      name: "side",
      title: "Side image",
      icon: objectRight,
      modelElements: ["imageBlock"],
      className: "image-style-side"
    };
  }
};
var DEFAULT_ICONS = {
  full: objectFullWidth,
  left: objectBlockLeft,
  right: objectBlockRight,
  center: objectCenter,
  inlineLeft: objectLeft,
  inlineRight: objectRight,
  inline: objectInline
};
var DEFAULT_DROPDOWN_DEFINITIONS = [{
  name: "imageStyle:wrapText",
  title: "Wrap text",
  defaultItem: "imageStyle:alignLeft",
  items: ["imageStyle:alignLeft", "imageStyle:alignRight"]
}, {
  name: "imageStyle:breakText",
  title: "Break text",
  defaultItem: "imageStyle:block",
  items: ["imageStyle:alignBlockLeft", "imageStyle:block", "imageStyle:alignBlockRight"]
}];
function normalizeStyles(config) {
  const configuredStyles = config.configuredStyles.options || [];
  const styles = configuredStyles.map((arrangement) => normalizeDefinition(arrangement)).filter((arrangement) => isValidOption(arrangement, config));
  return styles;
}
function getDefaultStylesConfiguration(isBlockPluginLoaded, isInlinePluginLoaded) {
  if (isBlockPluginLoaded && isInlinePluginLoaded) {
    return {
      options: [
        "inline",
        "alignLeft",
        "alignRight",
        "alignCenter",
        "alignBlockLeft",
        "alignBlockRight",
        "block",
        "side"
      ]
    };
  } else if (isBlockPluginLoaded) {
    return {
      options: ["block", "side"]
    };
  } else if (isInlinePluginLoaded) {
    return {
      options: ["inline", "alignLeft", "alignRight"]
    };
  }
  return {};
}
function getDefaultDropdownDefinitions(pluginCollection) {
  if (pluginCollection.has("ImageBlockEditing") && pluginCollection.has("ImageInlineEditing")) {
    return [...DEFAULT_DROPDOWN_DEFINITIONS];
  } else {
    return [];
  }
}
function normalizeDefinition(definition) {
  if (typeof definition === "string") {
    if (!DEFAULT_OPTIONS[definition]) {
      definition = { name: definition };
    } else {
      definition = { ...DEFAULT_OPTIONS[definition] };
    }
  } else {
    definition = extendStyle(DEFAULT_OPTIONS[definition.name], definition);
  }
  if (typeof definition.icon === "string") {
    definition.icon = DEFAULT_ICONS[definition.icon] || definition.icon;
  }
  return definition;
}
function isValidOption(option, { isBlockPluginLoaded, isInlinePluginLoaded }) {
  const { modelElements, name } = option;
  if (!modelElements || !modelElements.length || !name) {
    warnInvalidStyle({ style: option });
    return false;
  } else {
    const supportedElements = [isBlockPluginLoaded ? "imageBlock" : null, isInlinePluginLoaded ? "imageInline" : null];
    if (!modelElements.some((elementName) => supportedElements.includes(elementName))) {
      logWarning("image-style-missing-dependency", {
        style: option,
        missingPlugins: modelElements.map((name2) => name2 === "imageBlock" ? "ImageBlockEditing" : "ImageInlineEditing")
      });
      return false;
    }
  }
  return true;
}
function extendStyle(source, style) {
  const extendedStyle = { ...style };
  for (const prop in source) {
    if (!Object.prototype.hasOwnProperty.call(style, prop)) {
      extendedStyle[prop] = source[prop];
    }
  }
  return extendedStyle;
}
function warnInvalidStyle(info) {
  logWarning("image-style-configuration-definition-invalid", info);
}
var utils_default = {
  normalizeStyles,
  getDefaultStylesConfiguration,
  getDefaultDropdownDefinitions,
  warnInvalidStyle,
  DEFAULT_OPTIONS,
  DEFAULT_ICONS,
  DEFAULT_DROPDOWN_DEFINITIONS
};

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle/converters.js
function modelToViewStyleAttribute(styles) {
  return (evt, data, conversionApi) => {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }
    const newStyle = getStyleDefinitionByName(data.attributeNewValue, styles);
    const oldStyle = getStyleDefinitionByName(data.attributeOldValue, styles);
    const viewElement = conversionApi.mapper.toViewElement(data.item);
    const viewWriter = conversionApi.writer;
    if (oldStyle) {
      viewWriter.removeClass(oldStyle.className, viewElement);
    }
    if (newStyle) {
      viewWriter.addClass(newStyle.className, viewElement);
    }
  };
}
function viewToModelStyleAttribute(styles) {
  const nonDefaultStyles = {
    imageInline: styles.filter((style) => !style.isDefault && style.modelElements.includes("imageInline")),
    imageBlock: styles.filter((style) => !style.isDefault && style.modelElements.includes("imageBlock"))
  };
  return (evt, data, conversionApi) => {
    if (!data.modelRange) {
      return;
    }
    const viewElement = data.viewItem;
    const modelImageElement = first(data.modelRange.getItems());
    if (!modelImageElement) {
      return;
    }
    if (!conversionApi.schema.checkAttribute(modelImageElement, "imageStyle")) {
      return;
    }
    for (const style of nonDefaultStyles[modelImageElement.name]) {
      if (conversionApi.consumable.consume(viewElement, { classes: style.className })) {
        conversionApi.writer.setAttribute("imageStyle", style.name, modelImageElement);
      }
    }
  };
}
function getStyleDefinitionByName(name, styles) {
  for (const style of styles) {
    if (style.name === name) {
      return style;
    }
  }
}

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle/imagestyleediting.js
var ImageStyleEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageStyleEditing";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  init() {
    const { normalizeStyles: normalizeStyles2, getDefaultStylesConfiguration: getDefaultStylesConfiguration2 } = utils_default;
    const editor = this.editor;
    const isBlockPluginLoaded = editor.plugins.has("ImageBlockEditing");
    const isInlinePluginLoaded = editor.plugins.has("ImageInlineEditing");
    editor.config.define("image.styles", getDefaultStylesConfiguration2(isBlockPluginLoaded, isInlinePluginLoaded));
    this.normalizedStyles = normalizeStyles2({
      configuredStyles: editor.config.get("image.styles"),
      isBlockPluginLoaded,
      isInlinePluginLoaded
    });
    this._setupConversion(isBlockPluginLoaded, isInlinePluginLoaded);
    this._setupPostFixer();
    editor.commands.add("imageStyle", new ImageStyleCommand(editor, this.normalizedStyles));
  }
  /**
   * Sets the editor conversion taking the presence of
   * {@link module:image/image/imageinlineediting~ImageInlineEditing `ImageInlineEditing`}
   * and {@link module:image/image/imageblockediting~ImageBlockEditing `ImageBlockEditing`} plugins into consideration.
   */
  _setupConversion(isBlockPluginLoaded, isInlinePluginLoaded) {
    const editor = this.editor;
    const schema = editor.model.schema;
    const modelToViewConverter = modelToViewStyleAttribute(this.normalizedStyles);
    const viewToModelConverter = viewToModelStyleAttribute(this.normalizedStyles);
    editor.editing.downcastDispatcher.on("attribute:imageStyle", modelToViewConverter);
    editor.data.downcastDispatcher.on("attribute:imageStyle", modelToViewConverter);
    if (isBlockPluginLoaded) {
      schema.extend("imageBlock", { allowAttributes: "imageStyle" });
      editor.data.upcastDispatcher.on("element:figure", viewToModelConverter, { priority: "low" });
    }
    if (isInlinePluginLoaded) {
      schema.extend("imageInline", { allowAttributes: "imageStyle" });
      editor.data.upcastDispatcher.on("element:img", viewToModelConverter, { priority: "low" });
    }
  }
  /**
   * Registers a post-fixer that will make sure that the style attribute value is correct for a specific image type (block vs inline).
   */
  _setupPostFixer() {
    const editor = this.editor;
    const document = editor.model.document;
    const imageUtils = editor.plugins.get(ImageUtils);
    const stylesMap = new Map(this.normalizedStyles.map((style) => [style.name, style]));
    document.registerPostFixer((writer) => {
      let changed = false;
      for (const change of document.differ.getChanges()) {
        if (change.type == "insert" || change.type == "attribute" && change.attributeKey == "imageStyle") {
          let element = change.type == "insert" ? change.position.nodeAfter : change.range.start.nodeAfter;
          if (element && element.is("element", "paragraph") && element.childCount > 0) {
            element = element.getChild(0);
          }
          if (!imageUtils.isImage(element)) {
            continue;
          }
          const imageStyle = element.getAttribute("imageStyle");
          if (!imageStyle) {
            continue;
          }
          const imageStyleDefinition = stylesMap.get(imageStyle);
          if (!imageStyleDefinition || !imageStyleDefinition.modelElements.includes(element.name)) {
            writer.removeAttribute("imageStyle", element);
            changed = true;
          }
        }
      }
      return changed;
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle/imagestyleui.js
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-image/theme/imagestyle.css";
var ImageStyleUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageStyleEditing];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageStyleUI";
  }
  /**
   * Returns the default localized style titles provided by the plugin.
   *
   * The following localized titles corresponding with
   * {@link module:image/imagestyle/utils#DEFAULT_OPTIONS} are available:
   *
   * * `'Wrap text'`,
   * * `'Break text'`,
   * * `'In line'`,
   * * `'Full size image'`,
   * * `'Side image'`,
   * * `'Left aligned image'`,
   * * `'Centered image'`,
   * * `'Right aligned image'`
   */
  get localizedDefaultStylesTitles() {
    const t = this.editor.t;
    return {
      "Wrap text": t("Wrap text"),
      "Break text": t("Break text"),
      "In line": t("In line"),
      "Full size image": t("Full size image"),
      "Side image": t("Side image"),
      "Left aligned image": t("Left aligned image"),
      "Centered image": t("Centered image"),
      "Right aligned image": t("Right aligned image")
    };
  }
  /**
   * @inheritDoc
   */
  init() {
    const plugins = this.editor.plugins;
    const toolbarConfig = this.editor.config.get("image.toolbar") || [];
    const imageStyleEditing = plugins.get("ImageStyleEditing");
    const definedStyles = translateStyles(imageStyleEditing.normalizedStyles, this.localizedDefaultStylesTitles);
    for (const styleConfig of definedStyles) {
      this._createButton(styleConfig);
    }
    const definedDropdowns = translateStyles([
      ...toolbarConfig.filter(isObject_default),
      ...utils_default.getDefaultDropdownDefinitions(plugins)
    ], this.localizedDefaultStylesTitles);
    for (const dropdownConfig of definedDropdowns) {
      this._createDropdown(dropdownConfig, definedStyles);
    }
  }
  /**
   * Creates a dropdown and stores it in the editor {@link module:ui/componentfactory~ComponentFactory}.
   */
  _createDropdown(dropdownConfig, definedStyles) {
    const factory = this.editor.ui.componentFactory;
    factory.add(dropdownConfig.name, (locale) => {
      let defaultButton;
      const { defaultItem, items, title } = dropdownConfig;
      const buttonViews = items.filter((itemName) => definedStyles.find(({ name }) => getUIComponentName(name) === itemName)).map((buttonName) => {
        const button = factory.create(buttonName);
        if (buttonName === defaultItem) {
          defaultButton = button;
        }
        return button;
      });
      if (items.length !== buttonViews.length) {
        utils_default.warnInvalidStyle({ dropdown: dropdownConfig });
      }
      const dropdownView = createDropdown(locale, SplitButtonView);
      const splitButtonView = dropdownView.buttonView;
      const splitButtonViewArrow = splitButtonView.arrowView;
      addToolbarToDropdown(dropdownView, buttonViews, { enableActiveItemFocusOnDropdownOpen: true });
      splitButtonView.set({
        label: getDropdownButtonTitle(title, defaultButton.label),
        class: null,
        tooltip: true
      });
      splitButtonViewArrow.unbind("label");
      splitButtonViewArrow.set({
        label: title
      });
      splitButtonView.bind("icon").toMany(buttonViews, "isOn", (...areOn) => {
        const index = areOn.findIndex(identity_default);
        return index < 0 ? defaultButton.icon : buttonViews[index].icon;
      });
      splitButtonView.bind("label").toMany(buttonViews, "isOn", (...areOn) => {
        const index = areOn.findIndex(identity_default);
        return getDropdownButtonTitle(title, index < 0 ? defaultButton.label : buttonViews[index].label);
      });
      splitButtonView.bind("isOn").toMany(buttonViews, "isOn", (...areOn) => areOn.some(identity_default));
      splitButtonView.bind("class").toMany(buttonViews, "isOn", (...areOn) => areOn.some(identity_default) ? "ck-splitbutton_flatten" : void 0);
      splitButtonView.on("execute", () => {
        if (!buttonViews.some(({ isOn }) => isOn)) {
          defaultButton.fire("execute");
        } else {
          dropdownView.isOpen = !dropdownView.isOpen;
        }
      });
      dropdownView.bind("isEnabled").toMany(buttonViews, "isEnabled", (...areEnabled) => areEnabled.some(identity_default));
      this.listenTo(dropdownView, "execute", () => {
        this.editor.editing.view.focus();
      });
      return dropdownView;
    });
  }
  /**
   * Creates a button and stores it in the editor {@link module:ui/componentfactory~ComponentFactory}.
   */
  _createButton(buttonConfig) {
    const buttonName = buttonConfig.name;
    this.editor.ui.componentFactory.add(getUIComponentName(buttonName), (locale) => {
      const command = this.editor.commands.get("imageStyle");
      const view = new ButtonView(locale);
      view.set({
        label: buttonConfig.title,
        icon: buttonConfig.icon,
        tooltip: true,
        isToggleable: true
      });
      view.bind("isEnabled").to(command, "isEnabled");
      view.bind("isOn").to(command, "value", (value) => value === buttonName);
      view.on("execute", this._executeCommand.bind(this, buttonName));
      return view;
    });
  }
  _executeCommand(name) {
    this.editor.execute("imageStyle", { value: name });
    this.editor.editing.view.focus();
  }
};
function translateStyles(styles, titles) {
  for (const style of styles) {
    if (titles[style.title]) {
      style.title = titles[style.title];
    }
  }
  return styles;
}
function getUIComponentName(name) {
  return `imageStyle:${name}`;
}
function getDropdownButtonTitle(dropdownTitle, buttonTitle) {
  return (dropdownTitle ? dropdownTitle + ": " : "") + buttonTitle;
}

// node_modules/@ckeditor/ckeditor5-image/src/imagestyle.js
var ImageStyle = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageStyleEditing, ImageStyleUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageStyle";
  }
};
export {
  ImageStyle as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-image/src/imagestyle/imagestylecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagestyle/utils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagestyle/converters.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagestyle/imagestyleediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagestyle/imagestyleui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagestyle.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-image_src_imagestyle.js.map
